export default function PricingSection() {
  return (
    <section id="pricing" className="bg-background-100 py-16 md:py-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
            Unlock CareBridge Premium
          </h2>
          <p className="text-base md:text-lg text-foreground-600/80 max-w-2xl mx-auto leading-relaxed">
            Bring everyone together around Mom or Dad's care — one simple plan for the whole family.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Premium Monthly */}
          <div className="relative bg-white rounded-2xl p-6 md:p-8 border-2 border-secondary-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground-950">Premium Monthly</h3>
              <span className="bg-secondary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Most popular</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-secondary-500">$4.99</span>
              <span className="text-foreground-600/60 text-sm ml-1">/month</span>
            </div>
            <p className="text-sm text-accent-500 mb-6">7 days free, cancel anytime</p>
            <ul className="space-y-3">
              {[
                'Coordinate care across your whole family',
                'Track medications with reminders & refill alerts',
                'Log meals & hydration',
                'Wellness check-ins & recovery tracking',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-secondary-500 mt-0.5"></i>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Premium Yearly */}
          <div className="relative bg-white rounded-2xl p-6 md:p-8 border border-background-200/70">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground-950">Premium Yearly</h3>
              <span className="bg-accent-500/10 text-accent-500 text-xs font-semibold px-3 py-1 rounded-full">Best value</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-secondary-500">$49.99</span>
              <span className="text-foreground-600/60 text-sm ml-1">/year</span>
            </div>
            <p className="text-sm text-accent-500 mb-6">7 days free, save 17%</p>
            <ul className="space-y-3">
              {[
                'Coordinate care across your whole family',
                'Track medications with reminders & refill alerts',
                'Log meals & hydration',
                'Wellness check-ins & recovery tracking',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-secondary-500 mt-0.5"></i>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Discharge Plan */}
          <div className="relative bg-white rounded-2xl p-6 md:p-8 border border-background-200/70">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground-950">Discharge Plan</h3>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-secondary-500">$9.99</span>
              <span className="text-foreground-600/60 text-sm ml-1">one-off</span>
            </div>
            <p className="text-sm text-accent-500 mb-6">Single 30-day recovery plan</p>
            <ul className="space-y-3">
              {[
                '30-day recovery plan for one patient',
                'Medication coordination',
                'Transport & appointment planner',
                'Family visit coordination',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-secondary-500 mt-0.5"></i>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom trial card */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-background-200/70 max-w-3xl mx-auto text-center mb-8">
          <div className="mb-2">
            <span className="text-5xl font-bold text-secondary-500">$4.99</span>
            <span className="text-foreground-600/60 text-lg ml-1">/month</span>
          </div>
          <p className="text-sm text-accent-500 mb-8">7 days free, cancel anytime</p>

          <a
            href="https://carebridge4elderly.base44.app" target="_blank" rel="nofollow noopener noreferrer"
            className="w-full bg-secondary-500 text-white font-semibold px-8 py-4 rounded-full hover:bg-secondary-600 transition-all cursor-pointer text-base whitespace-nowrap mb-4 inline-block text-center"
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-sparkling-line"></i>
              Start 7-day free trial
            </span>
          </a>

          <p className="text-sm text-foreground-600/50 flex items-center justify-center gap-2">
            <i className="ri-shield-check-line"></i>
            Secure payment via Stripe. No charge for 7 days.
          </p>
        </div>

        {/* Demo section */}
        <div className="text-center">
          <p className="text-sm text-foreground-600/60 mb-4">Just exploring?</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a
              href="https://carebridge4elderly.base44.app" target="_blank" rel="nofollow noopener noreferrer"
              className="bg-white text-primary-500 font-semibold px-8 py-3 rounded-full border border-primary-500/20 hover:bg-primary-500/5 transition-all cursor-pointer text-sm whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-sparkling-line"></i>
              Try Premium Demo
            </a>
            <a
              href="https://carebridge4elderly.base44.app" target="_blank" rel="nofollow noopener noreferrer"
              className="bg-white text-primary-500 font-semibold px-8 py-3 rounded-full border border-primary-500/20 hover:bg-primary-500/5 transition-all cursor-pointer text-sm whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-hospital-line"></i>
              Try Discharge Demo
            </a>
          </div>
          <p className="text-sm text-foreground-600/50 mt-4">
            Instantly unlocks all premium features — no payment needed. For demo purposes.
          </p>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-foreground-600/30 text-center mt-10 max-w-2xl mx-auto">
          CareBridge does not provide medical advice. Always consult a qualified healthcare professional for medical concerns.
        </p>
      </div>
    </section>
  );
}